// Pure resolver (no server-only, no fetch) — depends on @workspace/model TYPES only.
// Turns resolved tenant theme tokens into a shadcn CSS-var map and a server-
// rendered inline <style> string so the correct brand paints on first render
// (US-012, no FOUC / hole F).
import type {
  ResolvedTheme,
  ThemeColorTokenKey,
  ThemeTokenKey,
} from "@workspace/model"

/**
 * A fully-resolved token set — every US-012 light token present, plus the `dark`
 * map of explicit dark-mode overrides (see resolveThemeTokens). Any color token
 * absent from `dark` keeps the algorithmic derivation in {@link renderThemeCss}.
 */
export type ResolvedThemeTokens = ResolvedTheme

/**
 * Maps each resolved theme token onto the shadcn CSS variable it drives in
 * packages/ui/globals.css. `fontFamily` drives `--font-sans` (which the tenant
 * token points at one of the preloaded `--font-sans-*` font vars, e.g.
 * `var(--font-sans-b)`); `radius` drives `--radius` (the `--radius-*` scale is
 * `calc()`-derived from it in globals.css).
 */
export const THEME_CSS_VAR_MAP: Record<ThemeTokenKey, string> = {
  primary: "--primary",
  background: "--background",
  foreground: "--foreground",
  accent: "--accent",
  muted: "--muted",
  radius: "--radius",
  fontFamily: "--font-sans",
}

/**
 * Color tokens the `.dark` block in globals.css also defines. These are emitted
 * light-only (`html:root:not(.dark)`); in dark mode the generic `.dark` palette
 * applies, with the brand tokens re-layered on top via
 * {@link DARK_BRAND_TRANSFORMS}.
 */
const COLOR_TOKEN_KEYS: ThemeTokenKey[] = [
  "primary",
  "background",
  "foreground",
  "accent",
  "muted",
]

/**
 * Dark-mode derivations for the brand tokens, using CSS relative color syntax
 * so the tenant's hue/chroma survive a dark toggle without the server having to
 * parse arbitrary CSS color values:
 * - `primary` is lifted into a lightness band readable on the dark background
 *   (and against the `.dark` `--primary-foreground`, which stays dark text).
 * - `accent` is pulled down toward the `.dark` hover-surface band while keeping
 *   the brand hue.
 * `foreground` intentionally stays the generic `.dark` near-white; the dark
 * surfaces are instead brand-tinted via {@link darkSurfaceDeclarations}. In
 * browsers without relative-color support the declaration is invalid and
 * drops, so the `.dark` palette is the graceful fallback.
 */
const DARK_BRAND_TRANSFORMS: Partial<
  Record<ThemeTokenKey, (value: string) => string>
> = {
  primary: (value) => `oklch(from ${value} clamp(0.72, l, 0.87) c h)`,
  accent: (value) => `oklch(from ${value} clamp(0.3, l, 0.45) c h)`,
}

/**
 * Dark-mode surface vars re-derived from the brand `primary` so dark mode isn't
 * flat black: each keeps the brand hue at a whisper of chroma (25% of the
 * primary's, capped at 0.03 so surfaces read as neutrals, and NOT floored so a
 * gray-brand tenant stays gray). Lightness steps mirror the generic `.dark`
 * scale, with `--background` lifted slightly (0.145 → 0.17) off pure black.
 * The hue comes from `primary` rather than the tenant `background` token
 * because light-mode backgrounds are near-white and carry no usable hue.
 */
const DARK_SURFACE_LIGHTNESS: Record<string, number> = {
  "--background": 0.17,
  "--card": 0.23,
  "--popover": 0.23,
  "--secondary": 0.29,
  "--muted": 0.29,
}

/**
 * Dark surface vars derived from `primary`'s hue at whisper chroma. `overrides`
 * lets an explicit tenant dark token (e.g. `dark.background`, `dark.muted`)
 * replace a derived surface outright while the un-overridden surfaces stay
 * brand-tinted.
 */
function darkSurfaceValue(primary: string, cssVar: string): string {
  return `oklch(from ${primary} ${DARK_SURFACE_LIGHTNESS[cssVar]} min(calc(c * 0.25), 0.03) h)`
}

function darkSurfaceDeclarations(
  primary: string,
  overrides: Partial<Record<string, string>> = {}
): string {
  return Object.keys(DARK_SURFACE_LIGHTNESS)
    .map((cssVar) => {
      const value = overrides[cssVar] ?? darkSurfaceValue(primary, cssVar)
      return `${cssVar}: ${value};`
    })
    .join(" ")
}

/**
 * Derive a readable text color from the color it sits on: the
 * `(0.65 - l) * 1000` term saturates the clamp to near-white (0.985) when the
 * base is darker than L≈0.65 and to near-black (0.145) when it is lighter, so
 * a tenant accent of any lightness keeps contrasting `--accent-foreground`
 * text in both modes (chroma 0 → neutral text). Accepts a full relative-color
 * expression as the base (they nest), which is how the dark rule feeds it the
 * dark-adapted accent.
 */
export function contrastForeground(base: string): string {
  return `oklch(from ${base} clamp(0.145, (0.65 - l) * 1000, 0.985) 0 h)`
}

/**
 * Secondary-text color for whatever `--muted` resolves to. Same saturating-clamp
 * flip as {@link contrastForeground} (dark base → light text, light base → dark
 * text), but the two branch endpoints SLIDE with the base lightness instead of
 * being fixed, because a fixed subdued band cannot hold 4.5:1 across arbitrary
 * tenant muted values: as the base approaches mid-tone, less and less contrast
 * is physically available, so the text has to run to the extremes.
 *
 * Both branch expressions are linear fits that dominate the measured WCAG-4.5:1
 * envelope over all in-gamut oklch bases:
 * - dark bases: `0.58 + (l - 0.15) * 1.05`, floored 0.58, capped 1.
 * - light bases: `0.115 + (l - 0.6) * 1.25`, floored 0, capped 0.5.
 * On the surfaces this file actually emits that leaves the text comfortably
 * subdued — light muted 0.97 → L 0.5 (5.5:1), dark muted 0.29 → L 0.727 (5.9:1)
 * — while a pathological mid-tone muted degrades to near-black/near-white.
 * KNOWN GAP: bases at l≈0.56–0.59 land 4.15–4.5:1; no foreground clears 4.5:1
 * there by much (max achievable ≈4.9), so mid-tone `muted` is best avoided.
 * A trace of the surface's own chroma is kept (25%, capped 0.02) so the text
 * doesn't read as a foreign gray on a tinted muted surface.
 */
function mutedForeground(base: string): string {
  const lightBase = `clamp(0, calc(0.115 + (l - 0.6) * 1.25), 0.5)`
  const darkBase = `clamp(0.58, calc(0.58 + (l - 0.15) * 1.05), 1)`
  return `oklch(from ${base} clamp(${lightBase}, (0.575 - l) * 1000, ${darkBase}) min(calc(c * 0.25), 0.02) h)`
}

/**
 * Tokens the `.dark` block does NOT touch (shape/typography). Emitted for both
 * light and dark so the tenant's radius + font survive a dark toggle.
 */
const INVARIANT_TOKEN_KEYS: ThemeTokenKey[] = ["radius", "fontFamily"]

/**
 * Strip characters that could break out of a CSS declaration/rule. Tenant theme
 * values are validated as strings by the US-004 zod layer but their CONTENT is
 * backend/config-authored, so guard against `;{}<>` injection into the inline
 * <style>. A value containing any of them is dropped (the token then simply keeps
 * whatever the stylesheet default is — never a broken page).
 */
function sanitizeCssValue(value: string): string | null {
  if (/[;{}<>\\]/.test(value)) return null
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

/**
 * Resolve tenant theme tokens → a flat `{ "--primary": "…" }` CSS-var map (all
 * tokens, light values). Used for the client seam / tests; the server no-flash
 * emission uses {@link renderThemeStyle} which splits light-only vs invariant.
 */
export function resolveThemeCssVars(
  theme: ResolvedThemeTokens
): Record<string, string> {
  const vars: Record<string, string> = {}
  for (const key of Object.keys(THEME_CSS_VAR_MAP) as ThemeTokenKey[]) {
    const value = sanitizeCssValue(theme[key])
    if (value !== null) vars[THEME_CSS_VAR_MAP[key]] = value
  }
  return vars
}

function declarations(
  theme: ResolvedThemeTokens,
  keys: ThemeTokenKey[],
  transform?: (key: ThemeTokenKey, value: string) => string
): string {
  return keys
    .map((key) => {
      const value = sanitizeCssValue(theme[key])
      if (value === null) return null
      const output = transform ? transform(key, value) : value
      return `${THEME_CSS_VAR_MAP[key]}: ${output};`
    })
    .filter((line): line is string => line !== null)
    .join(" ")
}

/**
 * Build the CSS text for the server-rendered inline `<style>` that applies the
 * tenant's brand on first paint (no FOUC of the wrong brand).
 *
 * Selectors are specificity-raised (`html:root` / `html:root:not(.dark)` /
 * `html:root.dark`) so the emitted rules win over globals.css regardless of
 * where the <style> lands in the document:
 * - invariant tokens (`--radius`, `--font-sans`) via `html:root` → both modes.
 * - color tokens via `html:root:not(.dark)` → light only; in dark mode the
 *   generic `.dark` palette applies for the neutrals…
 * - …while the brand tokens (`--primary`, `--accent`) are re-emitted via
 *   `html:root.dark`, dark-adapted through {@link DARK_BRAND_TRANSFORMS}, so the
 *   brand stays visible in dark mode.
 *
 * Both color rules also emit `--accent-foreground` contrast-derived from the
 * mode's effective accent (see {@link contrastForeground}), since the stylesheet
 * defaults assume the generic shadcn accent lightness, not the tenant's.
 */
export function renderThemeCss(theme: ResolvedThemeTokens): string {
  const invariant = declarations(theme, INVARIANT_TOKEN_KEYS)
  const light = declarations(theme, COLOR_TOKEN_KEYS)

  const accent = sanitizeCssValue(theme.accent)
  const lightAccentFg = accent
    ? ` --accent-foreground: ${contrastForeground(accent)};`
    : ""

  const muted = sanitizeCssValue(theme.muted)
  const lightMutedFg = muted
    ? ` --muted-foreground: ${mutedForeground(muted)};`
    : ""

  const rules: string[] = []
  if (invariant) rules.push(`html:root { ${invariant} }`)
  if (light)
    rules.push(
      `html:root:not(.dark) { ${light}${lightAccentFg}${lightMutedFg} }`
    )

  const dark = renderDarkRule(theme)
  if (dark) rules.push(dark)

  return rules.join("\n")
}

/** Read a sanitized explicit dark override for a color token (null when unset). */
function darkOverride(
  theme: ResolvedThemeTokens,
  key: ThemeColorTokenKey
): string | null {
  const raw = theme.dark[key]
  return typeof raw === "string" ? sanitizeCssValue(raw) : null
}

/**
 * Build the `html:root.dark` rule. Each color var is EXPLICIT when the tenant
 * supplied `theme.dark.<token>`, else DERIVED from the light value exactly as
 * before (so a tenant with no `dark` overrides renders byte-identically):
 * - `primary`/`accent`: explicit value, else {@link DARK_BRAND_TRANSFORMS}. When
 *   explicit, a contrast-derived `--*-foreground` rides along so arbitrary dark
 *   brand colors stay readable.
 * - surfaces (`background`/`card`/`popover`/`secondary`/`muted`): tinted from the
 *   effective dark primary, with `background`/`muted` overridable outright.
 * - `foreground`: emitted only when explicit (else the generic `.dark` near-white
 *   is kept, as before).
 */
function renderDarkRule(theme: ResolvedThemeTokens): string {
  const lightPrimary = sanitizeCssValue(theme.primary)
  const lightAccent = sanitizeCssValue(theme.accent)

  const explicitPrimary = darkOverride(theme, "primary")
  const explicitAccent = darkOverride(theme, "accent")
  const explicitBackground = darkOverride(theme, "background")
  const explicitForeground = darkOverride(theme, "foreground")
  const explicitMuted = darkOverride(theme, "muted")

  const effectivePrimary =
    explicitPrimary ??
    (lightPrimary ? DARK_BRAND_TRANSFORMS.primary!(lightPrimary) : null)
  const effectiveAccent =
    explicitAccent ??
    (lightAccent ? DARK_BRAND_TRANSFORMS.accent!(lightAccent) : null)

  const decls: string[] = []

  if (effectivePrimary) decls.push(`--primary: ${effectivePrimary};`)
  // Derived dark primary is lightness-lifted to sit on the `.dark`'s dark
  // primary-foreground; an EXPLICIT dark primary can be any lightness, so pin a
  // contrasting foreground to it.
  if (explicitPrimary)
    decls.push(`--primary-foreground: ${contrastForeground(explicitPrimary)};`)

  if (effectiveAccent) {
    decls.push(`--accent: ${effectiveAccent};`)
    decls.push(`--accent-foreground: ${contrastForeground(effectiveAccent)};`)
  }

  if (explicitForeground) decls.push(`--foreground: ${explicitForeground};`)

  // Surfaces derive from the explicit dark primary hue when given, else the
  // light primary (identical output to before for derived-only tenants).
  const surfaceSource = explicitPrimary ?? lightPrimary
  if (surfaceSource) {
    const surfaceOverrides: Record<string, string> = {}
    if (explicitBackground)
      surfaceOverrides["--background"] = explicitBackground
    if (explicitMuted) surfaceOverrides["--muted"] = explicitMuted
    decls.push(darkSurfaceDeclarations(surfaceSource, surfaceOverrides))
    decls.push(
      `--muted-foreground: ${mutedForeground(
        explicitMuted ?? darkSurfaceValue(surfaceSource, "--muted")
      )};`
    )
  } else {
    if (explicitBackground) decls.push(`--background: ${explicitBackground};`)
    if (explicitMuted) {
      decls.push(`--muted: ${explicitMuted};`)
      decls.push(`--muted-foreground: ${mutedForeground(explicitMuted)};`)
    }
  }

  if (decls.length === 0) return ""
  return `html:root.dark { ${decls.join(" ")} }`
}
